/**
 * The translation function used by every migrated call site.
 *
 * ## Why this exists rather than `useTranslation`
 *
 * A large share of this app's user-facing strings live **outside component bodies** — antd column
 * definitions, status maps, option arrays and validation-rule objects declared at module scope.
 * A hook cannot be called there. Rewriting all of those into hook-callable shapes would be a far
 * larger and riskier change than translating them, so instead every call site uses this plain
 * function, and keys carry their namespace (`t('payroll:runs.title')`) so one function serves all.
 *
 * ## How re-rendering works
 *
 * Because this is not a hook, React does not know a language change affects it. `LocaleProvider`
 * therefore remounts its subtree when the locale changes (see its `key`), which re-evaluates every
 * render path and every module-scope structure that is rebuilt during render.
 *
 * ## Ordering
 *
 * Catalogues are preloaded for the active locale before first paint (`initI18n`), so a call during
 * the very first render already has its namespace. Before i18next finishes initialising — which
 * only happens for code running at import time — the key's own default is returned rather than a
 * raw key.
 */

import i18next from 'i18next';

type Vars = Record<string, unknown>;

/**
 * The English catalogue, bundled statically.
 *
 * English is the source of truth and is needed as the fallback for every other language, so it is
 * never worth fetching — `eager: true` inlines it at build time. That makes `t()` correct even
 * before i18next initialises, which matters in three places: module-scope calls that run at import
 * time, unit tests that never boot the provider, and any runtime failure that leaves i18next
 * unready.
 *
 * Without this the fallback was a *humanised key*, which is subtly wrong rather than obviously
 * wrong: `t('approvals:flowmodel.claimAmount')` rendered as "Claim Amount" where the real copy is
 * "Claim amount". A test caught the discrepancy; on screen it would just have looked like sloppy
 * capitalisation.
 */
const EN_MODULES = import.meta.glob<{ default: Record<string, unknown> }>('./locales/en/*.json', {
  eager: true,
});

const flatten = (obj: Record<string, unknown>, prefix: string, out: Record<string, string>) => {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      flatten(v as Record<string, unknown>, key, out);
    } else if (typeof v === 'string') {
      out[key] = v;
    }
  }
  return out;
};

export const EN_CATALOGUE: Record<string, Record<string, string>> = {};
for (const [path, mod] of Object.entries(EN_MODULES)) {
  const ns = path.replace('./locales/en/', '').replace('.json', '');
  EN_CATALOGUE[ns] = flatten(mod.default, '', {});
}

/** English-only CLDR plural categories, for the fallback path below. */
const englishPluralCategory = (count: number): 'one' | 'other' =>
  new Intl.PluralRules('en').select(count) === 'one' ? 'one' : 'other';

const englishFor = (key: string, vars?: Vars): string | null => {
  const [ns, rest] = key.includes(':') ? key.split(/:(.+)/) : ['common', key];
  const catalogue = EN_CATALOGUE[ns];
  if (!catalogue) return null;
  // A count-bearing call (`t(key, { count })`) is resolved by real i18next via `key_one` /
  // `key_other` (English only has two CLDR categories, unlike Arabic's six) — this fallback
  // path is a flat lookup and has to do the same suffixing, or every pluralised key renders as
  // a humanised key here even though the live app translates it correctly. This is only hit
  // before i18next initialises, or in a unit test that never boots it.
  if (typeof vars?.count === 'number' && Number.isFinite(vars.count)) {
    const suffix = englishPluralCategory(vars.count);
    const plural = catalogue[`${rest}_${suffix}`] ?? catalogue[`${rest}_other`];
    if (plural !== undefined) return plural;
  }
  return catalogue[rest] ?? null;
};

/** Very small interpolation, matching i18next's `{{name}}` syntax, for the fallback path. */
const interpolate = (text: string, vars?: Vars): string =>
  vars ? text.replace(/\{\{(\w+)\}\}/g, (m, k) => (k in vars ? String(vars[k]) : m)) : text;

/**
 * Translate a namespace-qualified key: `t('payroll:runs.title')`.
 *
 * Resolution order: the active language → the bundled English → a readable rendering of the key.
 * Always returns a string; a user must never see `payroll:runs.title` on screen.
 */
export const t = (key: string, vars?: Vars): string => {
  if (i18next.isInitialized) {
    const value = i18next.t(key, vars as never);
    // i18next signals "missing" by returning the key — but it returns the key WITHOUT the
    // namespace prefix. Comparing only against the full key therefore treats that sentinel as a
    // successful translation, and the raw key reaches the screen.
    //
    // That is exactly what happened: `t('payroll:attendance.offDay')` rendered the literal text
    // `attendance.offDay` in the UI for every key present in English but missing in Arabic. Both
    // forms have to be treated as "not found" so the English fallback below can do its job.
    const withoutNs = key.includes(':') ? key.slice(key.indexOf(':') + 1) : key;
    if (typeof value === 'string' && value !== key && value !== withoutNs) return value;
  }
  const english = englishFor(key, vars);
  if (english !== null) return interpolate(english, vars);
  return humanize(key);
};

/** `payroll:runs.grossPay` → `Gross pay`. The last-resort fallback, never the normal path. */
const humanize = (key: string): string => {
  const last = key.split(':').pop()?.split('.').pop() ?? key;
  const spaced = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export default t;
